/**
 * Build formatValue and axis display options from chart axis config (leftY/rightY).
 * Matches InsightsChart behavior so funnels and insights use the same formatting.
 */
import { formatNumber as formatCompactNumber } from "shared/utils/numberFormatter";

/**
 * @param {object} axisConfig - config.axis.leftY or config.axis.rightY
 * @returns {(value: number) => string}
 */
export function makeFormatValue(axisConfig) {
  if (!axisConfig || typeof axisConfig !== "object") {
    return (value) => {
      const num = Number(value);
      if (isNaN(num)) return "0";
      return formatCompactNumber(num, 1);
    };
  }
  const decimals = axisConfig.decimals ?? ".0";
  const decimalPlaces = decimals === ".00" ? 2 : 0;
  const unit = axisConfig.unit ?? "#";
  const customUnit = axisConfig.customUnit ?? "";
  const useAbbreviation = axisConfig.abbreviation !== false;
  const prefix = unit === "$" ? "$" : unit === "%" ? "" : unit === "Custom" ? "" : "";
  const suffix = unit === "%" ? "%" : unit === "Custom" ? (customUnit || "") : "";

  return (value) => {
    const num = Number(value);
    if (isNaN(num)) return "0";
    const str = useAbbreviation
      ? formatCompactNumber(num, decimalPlaces || 1)
      : Number(num).toFixed(decimalPlaces).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return `${prefix}${str}${suffix}`;
  };
}

/**
 * @param {object} axisConfig - config.axis.leftY or config.axis.rightY
 * @returns {{ min: number|null, max: number|null }}
 */
export function getThresholdValues(axisConfig) {
  if (!axisConfig || typeof axisConfig !== "object") return { min: null, max: null };
  const minRaw = axisConfig.thresholdMin != null && String(axisConfig.thresholdMin).trim() !== "" ? axisConfig.thresholdMin : null;
  const maxRaw = axisConfig.thresholdMax != null && String(axisConfig.thresholdMax).trim() !== "" ? axisConfig.thresholdMax : null;
  const minNum = minRaw != null ? parseFloat(minRaw) : NaN;
  const maxNum = maxRaw != null ? parseFloat(maxRaw) : NaN;
  return { min: !isNaN(minNum) ? minNum : null, max: !isNaN(maxNum) ? maxNum : null };
}
